import {
	type ChatCounterpart,
	DESK_COUNTERPART,
	type IOwnershipContext,
	type ISupportMessage,
	type ISupportReplyContext,
} from '../interface/support-chat';
import { counterpartCopy } from './chat-counterpart-copy';
import { isOwnMessage } from './support-api';

// What a sender may still do to a message they have already sent.
//
// Pure and clock-injected, and mirrored on the admin side, because these rules decide whether
// a control is even offered. Getting them wrong client-side means either offering an action
// the server will refuse — which reads as a broken button — or hiding one the user is entitled
// to, which reads as the feature not working.
//
// The server is still the authority: every rule here is enforced again in SupportChatService,
// and the client never assumes its own answer is binding.

/** Matches Support:EditWindowMinutes. Five, not WhatsApp's fifteen — see below. */
export const EDIT_WINDOW_MINUTES = 5;

/** Matches Support:DeleteForEveryoneHours. */
export const DELETE_WINDOW_HOURS = 48;

export type EditRefusal = 'notYours' | 'alreadyDeleted' | 'windowClosed' | 'alreadyRead' | 'notYetSent';

export type DeleteRefusal = 'notYours' | 'alreadyDeleted' | 'windowClosed' | 'escalated' | 'notYetSent';

const minutesSince = (iso: string, now: number): number => (now - new Date(iso).getTime()) / 60_000;

/**
 * Whether a message may still be edited.
 *
 * <p>Five minutes rather than WhatsApp's fifteen: their window is tuned for social regret,
 * whereas the risk here is an admin who typed "PU 042" and meant "PU 043" and needs seconds to
 * catch it.</p>
 *
 * <p>Editing also stops the moment the other side has READ it. At that point someone may have
 * ACTED on the instruction, and a silent edit rewrites what they were told — in a record that
 * may later be read back during a dispute. The remedy after that is a correction message, which
 * leaves the original intact.</p>
 */
/**
 * Whether the reader sent the message.
 *
 * <p>`senderId === me` first, then — support threads only — the server's `senderSide`, which is
 * derived from the thread key. `clientId` is NOT a test: the DESK mints one on compose too and the
 * server persists it, so every stored message carries one — keying on it put Edit and Delete on
 * the support desk's own messages, which the server then refused as NotTheAuthor, and rendered
 * the desk's words back as the agent's own.</p>
 *
 * <p>A message still in the outbox has neither a server id nor, on an older server, a side; only
 * this device can have composed it, which is what the queued and sending states mark.</p>
 *
 * <p>Mirrors `isDeskOwn` in admin-frontend/src/app/support/utils/message-actions.ts, with the
 * side inverted.</p>
 */
export const isOwnBy = (message: ISupportMessage, ctx: IOwnershipContext): boolean => {
	// A row still in the outbox can only have been composed on this device.
	if (message.state === 'queued' || message.state === 'sending') return true;
	// One predicate for the whole app; the transcript, the badge and these controls must agree.
	return isOwnMessage(message, ctx);
};

export type ReplyRefusal = 'notYetSent' | 'alreadyDeleted';

/**
 * Whether a message can be replied to.
 *
 * <p>Available on BOTH sides — answering the desk's instruction is the main thing the feature is
 * for, so there is deliberately no ownership test here. That is what makes the ownership fix in
 * the two predicates below load-bearing: this predicate is what opens the action sheet to the
 * desk's messages, and those two have to be sound before it does.</p>
 */
export const canReplyToMessage = (
	message: Pick<ISupportMessage, 'id' | 'deletedAt'>
): { allowed: boolean; refusal?: ReplyRefusal } => {
	if (message.deletedAt) return { allowed: false, refusal: 'alreadyDeleted' };
	if (!message.id) return { allowed: false, refusal: 'notYetSent' };
	return { allowed: true };
};

/**
 * The quote to show for a message being replied to, built from the transcript row on hand.
 *
 * <p>The same shape the server returns, so the composer strip and the sent bubble render through
 * one component and cannot drift.</p>
 */
export const replyContextFrom = (message: ISupportMessage, ctx: IOwnershipContext): ISupportReplyContext => ({
	id: message.id ?? '',
	sequence: message.sequence,
	senderSide: message.senderSide ?? (isOwnBy(message, ctx) ? 'agent' : 'desk'),
	senderCoreUserId: message.senderId || (isOwnBy(message, ctx) ? ctx.me : null) || null,
	kind: message.kind,
	body: message.body,
	hasAttachments: !!message.attachments?.length,
	isDeleted: !!message.deletedAt,
	createdAt: message.createdAt,
});

export const canEditMessage = (
	message: ISupportMessage,
	ctx: IOwnershipContext,
	now: number = Date.now()
): { allowed: boolean; refusal?: EditRefusal } => {
	// isOwnBy, never `!message.clientId`. Both halves of the conversation mint a clientId and
	// the server persists it, so every stored message has one and that test passes for the desk's
	// messages too — offering Edit and Delete on an instruction this agent did not write.
	if (!isOwnBy(message, ctx)) return { allowed: false, refusal: 'notYours' };
	if (message.deletedAt) return { allowed: false, refusal: 'alreadyDeleted' };

	// A message still in the outbox has no server row to edit. Offering the control would
	// produce a 404 for something the user can simply retype.
	if (!message.id) return { allowed: false, refusal: 'notYetSent' };

	if (message.state === 'read') return { allowed: false, refusal: 'alreadyRead' };
	if (minutesSince(message.createdAt, now) > EDIT_WINDOW_MINUTES) return { allowed: false, refusal: 'windowClosed' };

	return { allowed: true };
};

/**
 * Whether a message may still be retracted for everyone.
 *
 * <p>Unlike editing, this stays available AFTER the message has been read — retraction is
 * precisely the thing you need once you realise the wrong person saw something.</p>
 */
export const canDeleteMessage = (
	message: ISupportMessage & { isEscalated?: boolean },
	ctx: IOwnershipContext,
	now: number = Date.now()
): { allowed: boolean; refusal?: DeleteRefusal } => {
	// See canEditMessage: ownership is the sender id's answer, then senderSide's, never clientId's.
	if (!isOwnBy(message, ctx)) return { allowed: false, refusal: 'notYours' };
	if (message.deletedAt) return { allowed: false, refusal: 'alreadyDeleted' };
	if (!message.id) return { allowed: false, refusal: 'notYetSent' };

	// Escalated into an incident: its attachments have been copied onto the incident record,
	// so destroying the source would leave election evidence pointing at nothing. The server
	// answers 409 for this; the client should not offer the control at all.
	if (message.isEscalated) return { allowed: false, refusal: 'escalated' };

	if (minutesSince(message.createdAt, now) > DELETE_WINDOW_HOURS * 60) return { allowed: false, refusal: 'windowClosed' };

	return { allowed: true };
};

/**
 * Why a control is unavailable, in the interface's voice.
 *
 * States what happened and what to do instead — never apologises, never says "cannot".
 */
export const editRefusalMessage = (refusal: EditRefusal, counterpart: ChatCounterpart = DESK_COUNTERPART): string => {
	switch (refusal) {
		case 'alreadyRead':
			return `${counterpartCopy(counterpart).subject} has already read this. Send a correction instead — editing it now would change what they were told.`;
		case 'windowClosed':
			return 'This message is too old to edit. Send a correction instead.';
		case 'alreadyDeleted':
			return 'This message was deleted.';
		case 'notYetSent':
			return 'This message has not sent yet.';
		default:
			return 'You can only edit your own messages.';
	}
};

export const deleteRefusalMessage = (refusal: DeleteRefusal): string => {
	switch (refusal) {
		case 'escalated':
			return 'This message has been escalated to an incident and can no longer be deleted.';
		case 'windowClosed':
			return `Messages can only be deleted for everyone within ${DELETE_WINDOW_HOURS} hours.`;
		case 'alreadyDeleted':
			return 'This message was already deleted.';
		case 'notYetSent':
			return 'This message has not sent yet.';
		default:
			return 'You can only delete your own messages.';
	}
};

/**
 * Tombstone text.
 *
 * <p>An admin retraction names the DESK rather than a person. An admin silently withdrawing an
 * instruction to a field agent is a governance event, not a typo fix, and naming the individual
 * would both misattribute it (admins rotate through the desk) and invite a personal reading of
 * an institutional act.</p>
 */
export const tombstoneText = (
	deletedBy: ISupportMessage['deletedBy'],
	own: boolean,
	counterpart: ChatCounterpart = DESK_COUNTERPART
): string => {
	if (own) return 'You deleted this message';
	if (counterpart.kind === 'desk') {
		return deletedBy === 'desk' ? 'Support desk deleted this message' : 'This message was deleted';
	}
	// A peer thread has exactly two people: a retraction that is not the reader's own is the
	// counterpart's. Named, because here it IS a person and not a function.
	if (deletedBy && deletedBy !== 'self') return `${counterpartCopy(counterpart).tombstoneActor} deleted this message`;
	return 'This message was deleted';
};
